import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFrameLoad } from "./AppFrameLoad";
import { AppIcons } from "./AppIcons";
import { AppPhoneChat } from "./AppPhoneChat";
import { AppTaskList, IAppTaskListListener } from "./AppTaskList";

// AppPhoneChatRun - the browser half of `AppPhoneChat`'s header buttons (owner 2026-11-01: "Move the tasks into a dialog
// also, like details and insights in the mobile ui"). On a phone the page's own header row and task strip are hidden
// (`AppPhoneChat.FrameCss`); the app's chat header carries Tasks / Details / Insights:
//   * Details / Insights press the page's OWN drawer labels (`label[for=wac-details]`, `label[for=wac-insights]`);
//   * Tasks opens the page's task strip as a bottom sheet (`data-wac-tasksheet` on the frame's <html>) with a title,
//     a close button and a backdrop, every task shown. Esc, the close button or the backdrop shut it.
// A button is shown only when the page has its target (an old page or a chat with no tasks shows fewer). ⛔ Nothing sends.

export class AppPhoneChatRun {
    private static Installed: boolean = false;
    /** Set by `AppMain`: opens the open chat's "Add task" dialog (`AppPhoneTaskAddRun`). */
    public static OnAddTask: (() => void) | null = null;

    /** One delegated click listener for the header buttons (the header is redrawn with every chat). */
    public static Install = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppPhoneChatRun.Installed === true) { return; }
            AppPhoneChatRun.Installed = true;
            document.addEventListener("click", AppPhoneChatRun.OnClick);
            // The keyboard comes with the message box's focus: shut the task sheet then, whether or not the viewport
            // has reported the keyboard yet (measured on the Android emulator: the sheet stayed over the keyboard).
            document.addEventListener("focusin", AppPhoneChatRun.OnFocusIn);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.Install");
        }
    };

    /** The contact frame loaded: show the buttons its page can serve, and give the strip its sheet parts. */
    public static Attach = (Frame: HTMLIFrameElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Frame.addEventListener("load", AppPhoneChatRun.OnFrameLoad);
            AppPhoneChatRun.OnFrameLoad();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.Attach");
        }
    };

    private static OnFrameLoad = (): void => {
        AppFrameLoad.Run(AppPhoneChatRun.Frame(), AppPhoneChatRun.OnDoc, "AppPhoneChatRun.OnFrameLoad");
    };

    private static OnDoc = (Doc: Document): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Strip: Element | null = Doc.querySelector("nav.tasks");
            const Count: Element | null = Doc.querySelector(".tcount");
            let Tasks: string = "";
            if (Count !== null && Count.textContent !== null) { Tasks = Count.textContent.replace(/[^0-9]/g, ""); }
            AppPhoneChatRun.Show("tasks", Strip !== null && Tasks.length > 0 && Tasks !== "0", Tasks);
            AppPhoneChatRun.Show("details", Doc.querySelector("label[for=\"wac-details\"]") !== null, "");
            AppPhoneChatRun.Show("insights", Doc.querySelector("label[for=\"wac-insights\"]") !== null, "");
            // A page with a task strip has tasks on: a task can be added for this chat.
            AppPhoneChatRun.Show("addtask", Strip !== null, "");
            if (Strip === null || Strip.querySelector(".wac-tshead") !== null) { return; }
            const Head: HTMLElement = Doc.createElement("div");
            Head.className = "wac-tshead";
            const Title: HTMLElement = Doc.createElement("span");
            Title.textContent = "Open tasks";
            const Close: HTMLElement = Doc.createElement("button");
            Close.setAttribute("type", "button");
            Close.className = "wac-tsclose";
            Close.setAttribute("aria-label", "Close");
            Close.innerHTML = AppIcons.Svg(AppIcons.Close, 16);
            Head.appendChild(Title);
            Head.appendChild(Close);
            Strip.insertBefore(Head, Strip.firstChild);
            const Back: HTMLElement = Doc.createElement("div");
            Back.className = "wac-tsback";
            Doc.body.appendChild(Back);
            Doc.addEventListener("click", AppPhoneChatRun.OnFrameClick);
            Doc.addEventListener("keydown", AppPhoneChatRun.OnFrameKey);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.OnDoc");
        }
    };

    private static OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (!(Ev.target instanceof Element)) { return; }
            const Btn: Element | null = Ev.target.closest("[" + AppPhoneChat.PageAttr + "]");
            if (Btn === null) { return; }
            const Frame: HTMLIFrameElement | null = AppPhoneChatRun.Frame();
            if (Frame === null || Frame.contentDocument === null) { return; }
            const Doc: Document = Frame.contentDocument;
            const Kind: string | null = Btn.getAttribute(AppPhoneChat.PageAttr);
            if (Kind === "addtask") {
                AppPhoneChatRun.CloseOthers(Doc, "");
                if (AppPhoneChatRun.OnAddTask !== null) { AppPhoneChatRun.OnAddTask(); }
                return;
            }
            // ⛔ ONE AT A TIME (owner 2026-11-01): opening one closes the other two.
            AppPhoneChatRun.CloseOthers(Doc, nz(Kind));
            if (Kind === "tasks") { AppPhoneChatRun.Sheet(Doc, Doc.documentElement.hasAttribute(AppPhoneChat.SheetAttr) === false); return; }
            const Label: HTMLElement | null = Doc.querySelector("label[for=\"wac-" + Kind + "\"]") as HTMLElement | null;
            if (Label !== null) { Label.click(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.OnClick");
        }
    };

    private static OnFrameClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (!(Ev.target instanceof Element)) { return; }
            if (Ev.target.closest(".wac-tsclose, .wac-tsback") === null) { return; }
            AppPhoneChatRun.Sheet(Ev.target.ownerDocument, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.OnFrameClick");
        }
    };

    private static OnFrameKey = (Ev: KeyboardEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Ev.key !== "Escape" || !(Ev.target instanceof Node) || Ev.target.ownerDocument === null) { return; }
            if (Ev.target.ownerDocument.documentElement.hasAttribute(AppPhoneChat.SheetAttr) === false) { return; }
            AppPhoneChatRun.Sheet(Ev.target.ownerDocument, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.OnFrameKey");
        }
    };

    /** Shuts every panel but `Keep` (tasks | details | insights): the sheet and the page's own drawer boxes. */
    private static OnFocusIn = (Ev: FocusEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target = Ev.target as Element | null;
            if (Target === null || typeof Target.closest !== "function" || Target.closest("#wac-say") === null) { return; }
            const Frame = AppPhoneChatRun.Frame();
            if (Frame === null) { return; }
            const Doc = Frame.contentDocument;
            if (Doc !== null &&Doc.documentElement.hasAttribute(AppPhoneChat.SheetAttr) === true) { AppPhoneChatRun.Sheet(Doc, false); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.OnFocusIn");
        }
    };

    private static CloseOthers =(Doc: Document, Keep: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Keep !== "tasks") { AppPhoneChatRun.Sheet(Doc, false); }
            for (const Kind of ["details", "insights"]) {
                if (Kind === Keep) { continue; }
                const Box: HTMLInputElement | null = Doc.getElementById("wac-" + Kind) as HTMLInputElement | null;
                if (Box !== null && Box.checked === true) { Box.checked = false; Box.dispatchEvent(new Event("change", { bubbles: true })); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.CloseOthers");
        }
    };

    /** Opens / shuts the task sheet; open = every task (the strip's own "+N more" box ticked). */
    private static Sheet = (Doc: Document, Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Open === false) { Doc.documentElement.removeAttribute(AppPhoneChat.SheetAttr); return; }
            const More: HTMLInputElement | null = Doc.getElementById("wac-tmore") as HTMLInputElement | null;
            if (More !== null) { More.checked = true; }
            Doc.documentElement.setAttribute(AppPhoneChat.SheetAttr, "1");
            const Close: HTMLElement | null = Doc.querySelector(".wac-tsclose") as HTMLElement | null;
            if (Close !== null) { Close.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.Sheet");
        }
    };

    /** `AppPhoneTaskBadge`'s way in: the header Tasks button shown with `Count`, or hidden at 0. */
    public static ShowTasks = (Count: number): void => {
        AppPhoneChatRun.Show("tasks", Count > 0, String(Count));
    };

    private static Show = (Kind: string, On: boolean, Count: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Btn: HTMLElement | null = document.querySelector("[" + AppPhoneChat.PageAttr + "=\"" + Kind + "\"]") as HTMLElement | null;
            if (Btn === null) { return; }
            Btn.hidden = On === false;
            const Badge: Element | null = Btn.querySelector(".chpagecount");
            if (Badge !== null) { Badge.textContent = Count; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChatRun.Show");
        }
    };

    private static Frame = (): HTMLIFrameElement | null => document.getElementById("wac-frame") as HTMLIFrameElement | null;
}

/** Where the badge goes: the header's Tasks button (`AppPhoneChatRun.ShowTasks`), or a test's recorder. */
export interface IAppPhoneTaskShow {
    ShowTasks(Count: number): void;
}

export class AppPhoneChatTaskShow implements IAppPhoneTaskShow {
    public ShowTasks = (Count: number): void => {
        AppPhoneChatRun.ShowTasks(Count);
    };
}

/**
 * ⭐ TASKS ARE OBJECTS (owner 2026-11-02): the phone header's Tasks badge LISTENS to the open chat's task list
 * (`AppTaskFrame.List`) - a ticked task takes the "1" away at once. No watching of the page.
 */
export class AppPhoneTaskBadge implements IAppTaskListListener {
    private Target: IAppPhoneTaskShow;

    public constructor(Target: IAppPhoneTaskShow) {
        this.Target = Target;
    }

    public Changed = (List: AppTaskList): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Target.ShowTasks(List.OpenCount());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskBadge.Changed");
        }
    };
}
